import React, { useEffect, useState } from 'react';
import { Drawer, Form, Select, Button, Space, message, Tag, Spin } from 'antd';
import partnerNetworkService from '../../services/partnerNetworkService';

export default function AssignRmDrawer({ open, partner, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [rms, setRms] = useState([]);
  const [loadingRms, setLoadingRms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentRmId, setCurrentRmId] = useState(null);

  const partnerUuid = partner?.id ?? partner?.uuid;
  const currentRmName =
    partner?.manager_name && partner.manager_name !== 'Not Assigned'
      ? partner.manager_name
      : null;

  useEffect(() => {
    if (!open || !partnerUuid) return;

    let cancelled = false;

    const load = async () => {
      setLoadingRms(true);
      form.resetFields();
      setCurrentRmId(null);

      try {
        const [eligible, existing] = await Promise.all([
          partnerNetworkService.getEligibleRms(),
          partnerNetworkService.getRmForPartner(partnerUuid).catch(() => null),
        ]);

        if (cancelled) return;

        const list = Array.isArray(eligible) ? eligible : [];
        const existingRm = existing?.data ?? existing ?? null;

        let resolvedList = list;
        let selectedRmId = null;

        if (existingRm) {
          selectedRmId =
            existingRm.rm_id ??
            list.find(
              (r) =>
                (existingRm.rm_name && r.name === existingRm.rm_name) ||
                (existingRm.rm_email && r.email === existingRm.rm_email)
            )?.id ??
            null;

          if (
            selectedRmId &&
            !list.some((r) => r.id === selectedRmId)
          ) {
            resolvedList = [
              {
                id: selectedRmId,
                name: existingRm.rm_name ?? 'Assigned RM',
                email: existingRm.rm_email,
              },
              ...list,
            ];
          }
        }

        setRms(resolvedList);

        if (selectedRmId) {
          setCurrentRmId(selectedRmId);
          form.setFieldsValue({ rm_id: selectedRmId });
        }
      } catch (e) {
        if (cancelled) return;
        message.error(e?.response?.data?.message || e?.message || 'Failed to load RMs');
        setRms([]);
      } finally {
        if (!cancelled) setLoadingRms(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, partnerUuid, form]);

  const handleClose = () => {
    form.resetFields();
    setCurrentRmId(null);
    onClose?.();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!partnerUuid) {
        message.error('Partner identifier missing');
        return;
      }

      setSubmitting(true);
      const res = await partnerNetworkService.assignRm(partnerUuid, values.rm_id);

      if (res?.success === false) {
        message.error(res?.message || 'Failed to assign RM');
        return;
      }

      message.success(res?.message || 'RM assigned successfully');
      onSuccess?.();
      handleClose();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Failed to assign RM');
    } finally {
      setSubmitting(false);
    }
  };

  const partnerName = partner?.name || 'Partner';

  return (
    <Drawer
      title={currentRmName ? `Change RM — ${partnerName}` : `Assign RM — ${partnerName}`}
      size={420}
      open={open}
      onClose={handleClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Save
          </Button>
        </Space>
      }
    >
      <div className="mb-4">
        <span className="text-xs text-gray-500 mr-2">Currently:</span>
        {currentRmName ? (
          <Tag color="green" className="m-0 border-0 font-medium">
            {currentRmName}
          </Tag>
        ) : (
          <span className="text-gray-400 italic text-sm">Not assigned</span>
        )}
      </div>

      <Form form={form} layout="vertical">
        <Form.Item
          name="rm_id"
          label="Relationship Manager"
          rules={[{ required: true, message: 'Select an RM' }]}
        >
          <Select
            placeholder={loadingRms ? 'Loading RMs…' : 'Select RM'}
            loading={loadingRms}
            disabled={loadingRms}
            showSearch
            optionFilterProp="label"
            notFoundContent={loadingRms ? <Spin size="small" /> : 'No RMs found'}
            options={rms.map((r) => ({
              label: r.name,
              value: r.id,
            }))}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
